/**
 * Where the account hub's "Back to …" link goes.
 *
 * Inside /settings the side nav swaps to the hub's sections, so the way out
 * has to be explicit. It returns to the page you were on before you entered,
 * named, and it stays put while you move between sections. The browser's back
 * button would step back through each section instead. With no earlier page
 * in this tab (an email link, a new tab) it falls back to Home.
 *
 * Stored per tab in sessionStorage, so two tabs never fight over it, a
 * refresh inside the hub keeps it, and it survives the app swapping layouts
 * between a discovery page and the hub.
 */

export interface HubReturn {
  /** Path plus query of the page to go back to. */
  to: string
  /** What to call it: "Back to {label}". */
  label: string
}

export const HUB_HOME: HubReturn = { to: '/dashboard', label: 'Home' }

const STORAGE_KEY = 'carousel_hub_return'

/** True for the account hub's own pages. */
export function isHubPath(pathname: string): boolean {
  return pathname === '/settings' || pathname.startsWith('/settings/')
}

/** Names for the app's own places, whose page titles are generic or missing. */
const KNOWN: { match: (p: string) => boolean; label: string }[] = [
  { match: (p) => p === '/' || p === '/dashboard', label: 'Home' },
  { match: (p) => p === '/news' || p.startsWith('/news/'), label: 'News' },
  { match: (p) => p === '/compete', label: 'Compete' },
  { match: (p) => p === '/orgs', label: 'Organizations' },
  { match: (p) => p === '/trust' || p.startsWith('/trust/'), label: 'Trust score' },
  { match: (p) => p === '/notifications' || p.startsWith('/notifications/'), label: 'Notifications' },
  { match: (p) => p === '/passes' || p.startsWith('/pass/'), label: 'Passes' },
]

/** Strip the " | Carousel" suffix; a bare "Carousel" says nothing about the page. */
export function cleanTitle(title: string): string {
  const name = title.replace(/\s*\|\s*Carousel\s*$/, '').trim()
  return name === 'Carousel' ? '' : name
}

/**
 * The name for the page at `pathname`. The app's own places use fixed names.
 * Anything else (an org page, a competition) uses the title it set, and failing
 * that a player profile reads as the username. Last resort: "previous page".
 */
export function labelFor(pathname: string, title: string): string {
  const known = KNOWN.find((k) => k.match(pathname))
  if (known) return known.label
  const fromTitle = cleanTitle(title)
  if (fromTitle) return fromTitle
  const profile = /^\/u\/([^/]+)/.exec(pathname)
  if (profile) return decodeURIComponent(profile[1])
  return 'previous page'
}

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function session(): Store | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.sessionStorage
  } catch {
    return undefined
  }
}

interface Stored extends HubReturn {
  /** The page's path without its query, to match title updates against. */
  path: string
}

function readStored(store: Store | undefined): Stored | null {
  try {
    const raw = store?.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<Stored>
    if (typeof parsed.to === 'string' && parsed.to.startsWith('/') && !parsed.to.startsWith('//')
      && !isHubPath(parsed.to.split(/[?#]/)[0])
      && typeof parsed.label === 'string' && parsed.label
      && typeof parsed.path === 'string') {
      return { to: parsed.to, label: parsed.label, path: parsed.path }
    }
  } catch {
    // Corrupt or blocked storage: treat as nothing stored.
  }
  return null
}

function writeStored(value: Stored, store: Store | undefined): void {
  try {
    store?.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Private mode or full storage: the link falls back to Home.
  }
}

/**
 * Record a page outside the hub as the one to go back to. Called for every
 * such page, so whatever you last looked at before entering is what the hub
 * returns to; hub pages never overwrite it, so moving between sections
 * doesn't either.
 */
export function rememberPage(pathname: string, search: string, title: string, store: Store | undefined = session()): void {
  if (isHubPath(pathname)) return
  writeStored({ to: pathname + search, path: pathname, label: labelFor(pathname, title) }, store)
}

/** A page set its title after it was recorded: name it by that title. */
export function rememberTitle(pathname: string, title: string, store: Store | undefined = session()): void {
  const stored = readStored(store)
  if (!stored || stored.path !== pathname) return
  writeStored({ ...stored, label: labelFor(pathname, title) }, store)
}

/** Where the hub's back link goes: the last page outside the hub, or Home. */
export function readHubReturn(store: Store | undefined = session()): HubReturn {
  const stored = readStored(store)
  return stored ? { to: stored.to, label: stored.label } : HUB_HOME
}
